import type { Artwork } from "../types";
import { SEED_IMAGES } from "../data/seedImages";
import { apiMode, apiUrl } from "./api";

function placeholderImage(title?: string): string {
  const label = (title || "Artwork").replace(/&/g, "and").slice(0, 36);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="1000" viewBox="0 0 800 1000"><defs><linearGradient id="bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#d9d3c8"/><stop offset="100%" stop-color="#f1ece3"/></linearGradient></defs><rect width="800" height="1000" fill="url(#bg)"/><rect x="38" y="38" width="724" height="924" fill="none" stroke="#b8ad9a" stroke-width="2"/><text x="400" y="474" font-family="Georgia, serif" font-size="52" fill="#6e675b" text-anchor="middle">${label}</text><text x="400" y="536" font-family="Arial, sans-serif" font-size="22" letter-spacing="3" fill="#8b8375" text-anchor="middle">IMAGE COMING SOON</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/** Resolve the display image for an artwork (uploaded data URL or bundled seed). */
export function artImage(
  art: Pick<Artwork, "image" | "seedKey" | "imageId" | "title">,
): string {
  if (art.image) return art.image;
  if (art.seedKey && SEED_IMAGES[art.seedKey]) return SEED_IMAGES[art.seedKey];
  if (apiMode && art.imageId) return apiUrl(`/api/images/${art.imageId}`);
  return placeholderImage(art.title);
}

/**
 * Read a File, downscale it to a sane max dimension, and return a JPEG data URL.
 * Keeps localStorage small and uploads fast. Falls back to the raw data URL if
 * canvas processing fails for any reason.
 */
export function fileToDataUrl(
  file: File,
  maxDim = 1600,
  quality = 0.85,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const raw = reader.result as string;
      const img = new Image();
      img.onerror = () => resolve(raw);
      img.onload = () => {
        try {
          const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) return resolve(raw);
          ctx.drawImage(img, 0, 0, w, h);
          const out = canvas.toDataURL("image/jpeg", quality);
          resolve(out.length < raw.length ? out : raw);
        } catch {
          resolve(raw);
        }
      };
      img.src = raw;
    };
    reader.readAsDataURL(file);
  });
}

export function newId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}
